import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { axiosInstance } from '../../helpers'
import { Lead, LeadTour, LeadTourStatus } from '../../models/Outreach/types'

export type LeadTourPayload = {
    status?: LeadTourStatus
    starts_at?: string | null
    ends_at?: string | null
    given_by_id?: number | null
    notes?: string
}

const invalidateLeadQueries = (
    queryClient: ReturnType<typeof useQueryClient>,
) => {
    queryClient.invalidateQueries({ queryKey: ['leads'], exact: false })
    queryClient.invalidateQueries({
        queryKey: ['leasing-season-pipeline-counts'],
        exact: false,
    })
    queryClient.invalidateQueries({ queryKey: ['lead-notes'], exact: false })
}

export function useLeadTour() {
    const queryClient = useQueryClient()

    const createMutation = useMutation({
        mutationFn: async ({
            leadId,
            payload,
        }: {
            leadId: number
            payload: LeadTourPayload
        }) => {
            const { data } = await axiosInstance.post<{
                tour: LeadTour
                lead: Lead
            }>(`/outreach/leads/${leadId}/tours/`, payload)
            return data
        },
        onSuccess: (data, variables) => {
            invalidateLeadQueries(queryClient)
            queryClient.setQueryData(
                ['person-file-lead', variables.leadId],
                data.lead,
            )
            if ((variables.payload.status ?? 'scheduled') === 'skipped') {
                toast.success('Tour marked as skipped')
            } else {
                toast.success('Tour scheduled')
            }
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(error?.response?.data?.error ?? 'Failed to create tour')
        },
    })

    const updateMutation = useMutation({
        mutationFn: async ({
            leadId,
            tourId,
            payload,
        }: {
            leadId: number
            tourId: number
            payload: LeadTourPayload
        }) => {
            const { data } = await axiosInstance.patch<{
                tour: LeadTour
                lead: Lead
            }>(`/outreach/leads/${leadId}/tours/${tourId}/`, payload)
            return data
        },
        onSuccess: (data, variables) => {
            invalidateLeadQueries(queryClient)
            queryClient.setQueryData(
                ['person-file-lead', variables.leadId],
                data.lead,
            )
            const status = variables.payload.status
            if (status === 'completed') {
                toast.success('Tour marked completed')
            } else if (status === 'no_show') {
                toast.success('Tour marked as no-show')
            } else if (status === 'skipped') {
                toast.success('Tour marked as skipped')
            } else {
                toast.success('Tour updated')
            }
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(error?.response?.data?.error ?? 'Failed to update tour')
        },
    })

    return {
        createLeadTour: createMutation.mutateAsync,
        isCreatingLeadTour: createMutation.isLoading,
        updateLeadTour: updateMutation.mutateAsync,
        isUpdatingLeadTour: updateMutation.isLoading,
    }
}
